import { filesize } from 'filesize';

export const toBase64: (file: File) => Promise<string> = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const result = reader.result as string;
      const data = result.split(',')[1];
      resolve(data);
    };
    reader.onerror = (error) => reject(error);
  });

export const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);

    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => reject(error);
  });

export const isImageFile = (fileExtension: string) => {
  const pattern = /jpeg|jpg|gif|png$/i;
  return pattern.test(fileExtension);
};

export type FileType = {
  fileName: string;
  fileBase64: string;
  size: number;
  fileType: string;
};

export const collectFiles = async (
  files: File[] | FileList,
): Promise<FileType[]> => {
  const filePromises = Array.from(files).map(async (file) => ({
    fileName: file.name,
    fileBase64: await toBase64(file),
    size: file.size,
    fileType: file.type,
  }));

  return Promise.all(filePromises);
};

export const FILE_SIZE_LIMIT = 3.5 * 1024 * 1024;

export const MAX_FILE_SIZE = 5242880;

export const FILE_ACCEPT_TYPES = 'image/*,.pdf';

export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
] as const;

export const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
] as const;

export const NON_IMAGE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd',
] as const;

export const PDF_EXTENSION = 'pdf';

export const FILE_TYPE_IMAGE = 'IMAGE';

export const FILE_TYPE_PDF = 'PDF';

export const fileSizeFormatter = (size: number): string => {
  return filesize(size, { base: 2, standard: 'jedec' }) as string;
};

export function getFileNameWithoutExtension(fileName: string): string {
  if (fileName.endsWith('.')) {
    return fileName;
  }
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) {
    return fileName;
  }
  return fileName.substring(0, dotIndex);
}

export const getFileType: (fileName: string) => string = (filename) => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === 0) {
    return '';
  }
  return filename.substring(dotIndex + 1);
};

export type ContentDocument = {
  Title?: string | null;
  FileType?: string | null;
  FileExtension?: string | null;
};

export type FileCheckParams = {
  file: File | null;
  fileUrl: string | null;
  contentDoc?: ContentDocument | null;
};

const checkImageFileByType = (fileType: string): boolean | null => {
  if (
    IMAGE_MIME_TYPES.includes(fileType as (typeof IMAGE_MIME_TYPES)[number])
  ) {
    return true;
  }
  if (NON_IMAGE_MIME_TYPES.some((type) => fileType.startsWith(type))) {
    return false;
  }
  return null;
};

const checkImageFileByUrl = (
  fileUrl: string,
  fileName: string,
): boolean | null => {
  if (fileUrl.startsWith('data:image')) {
    return true;
  }

  const extension = fileName.split('.').pop()?.toLowerCase();

  if (
    extension &&
    IMAGE_EXTENSIONS.includes(extension as (typeof IMAGE_EXTENSIONS)[number])
  ) {
    return true;
  }

  if (extension === PDF_EXTENSION) {
    return false;
  }

  return null;
};

const checkImageFileByContentDoc = (
  contentDoc: ContentDocument,
): boolean | null => {
  const fileType = contentDoc.FileType?.toLowerCase();
  if (
    fileType === FILE_TYPE_IMAGE.toLowerCase() ||
    fileType?.startsWith('image/')
  ) {
    return true;
  }
  if (
    fileType === FILE_TYPE_PDF.toLowerCase() ||
    fileType?.includes(PDF_EXTENSION.toLowerCase())
  ) {
    return false;
  }
  const extension = contentDoc.FileExtension?.toLowerCase();
  if (
    extension &&
    IMAGE_EXTENSIONS.includes(extension as (typeof IMAGE_EXTENSIONS)[number])
  ) {
    return true;
  }
  return null;
};

export const isImageFileFromParams = ({
  file,
  fileUrl,
  contentDoc,
}: FileCheckParams): boolean => {
  if (!file && !contentDoc && !fileUrl) return false;

  if (file?.type) {
    const result = checkImageFileByType(file.type);
    if (result !== null) return result;
  }

  if (fileUrl) {
    const fileName = file?.name || contentDoc?.Title || '';
    const result = checkImageFileByUrl(fileUrl, fileName);
    if (result !== null) return result;
  }

  if (contentDoc) {
    const result = checkImageFileByContentDoc(contentDoc);
    if (result !== null) return result;
  }

  return false;
};
